import { Component, Input, computed, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlay, faPause } from '@fortawesome/free-solid-svg-icons';
import { SocketService, DraftSettings, Player } from '../../../../services/socket';
import { AuthService } from '../../../../services/auth';
import { GavelOptions } from '../gavel-options/gavel-options';
import { Subscription } from 'rxjs';

type StageView =
  | 'not_started'
  | 'choose_player'
  | 'waiting_for_nomination'
  | 'bid_on_player'
  | 'player_drafted'
  | 'draft_ended'
  | 'draft_paused';

@Component({
  selector: 'app-draft-stage',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, GavelOptions],
  templateUrl: './draft-stage.html',
  styleUrl: './draft-stage.css'
})
export class DraftStage implements OnInit, OnDestroy {
  @Input() leagueId!: number;
  @Input() isCommissioner = false;
  @Input() teamBudget: number = 200;
  @Input() maxBid: number | null = null;

  // FontAwesome icons
  faPlay = faPlay;
  faPause = faPause;

  // Player search state
  playerSearchQuery = signal('');
  searchResults = signal<Player[]>([]);
  selectedPlayer = signal<Player | null>(null);
  startingBid = signal(1);
  isSearching = signal(false);
  customBidAmount = signal(0);

  private subscriptions: Subscription[] = [];
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  // State for commissioner override UI
  showCommissionerOverride = signal(false);

  // State for commissioner bid-on-behalf UI
  bidOnBehalfUserId = signal<number | null>(null);

  // Computed name of the selected bid-on-behalf owner
  bidOnBehalfUserName = computed(() => {
    const targetUserId = this.bidOnBehalfUserId();
    if (!targetUserId) return null;
    const settings = this.socketService.draftSettings();
    const team = settings?.teams.find(t => t.user_id === targetUserId);
    return team?.user_name ?? null;
  });

  // Computed max bid for the selected bid-on-behalf owner
  bidOnBehalfMaxBid = computed(() => {
    const targetUserId = this.bidOnBehalfUserId();
    if (!targetUserId) return null;

    const settings = this.socketService.draftSettings();
    if (!settings?.teams) return null;

    const targetTeam = settings.teams.find(t => t.user_id === targetUserId);
    if (!targetTeam) return null;

    const userDraftHistory = (settings.draftHistory || []).filter(
      entry => entry.team_id === targetTeam.team_id
    );
    const spent = userDraftHistory.reduce((sum, entry) => sum + entry.cost, 0);
    const playersDrafted = userDraftHistory.length;
    const budget = settings.teamBudget ?? this.teamBudget;
    const rosterSize = settings.rosterSize;
    const remaining = budget - spent;
    const rosterSpotsNeeded = rosterSize ? rosterSize - playersDrafted : 1;
    const maxBid = rosterSpotsNeeded > 1 ? remaining - (rosterSpotsNeeded - 1) : remaining;
    return Math.max(0, maxBid);
  });

  // Computed signal for current nominator info (for commissioner override)
  currentNominatorInfo = computed(() => {
    const settings = this.socketService.draftSettings();
    if (!settings?.currentNominatorUserId || !settings.teams) return null;

    const nominatorTeam = settings.teams.find(t => t.user_id === settings.currentNominatorUserId);
    return nominatorTeam ? {
      userId: nominatorTeam.user_id,
      userName: nominatorTeam.user_name,
      teamName: nominatorTeam.team_name
    } : null;
  });

  // Computed stage view based on draft state
  stageView = computed<StageView>(() => {
    const settings = this.socketService.draftSettings();
    const auction = this.socketService.currentAuction();
    const currentUser = this.authService.currentUser();

    if (!settings) return 'not_started';

    switch (settings.status) {
      case 'not_started':
        return 'not_started';
      case 'paused':
        return 'draft_paused';
      case 'ended':
        return 'draft_ended';
      case 'in_progress':
        if (auction) {
          return 'bid_on_player';
        }
        if (settings.lastAuctionResult) {
          return 'player_drafted';
        }
        if (settings.currentNominatorUserId === currentUser?.id) {
          return 'choose_player';
        }
        return 'waiting_for_nomination';
      default:
        return 'not_started';
    }
  });

  constructor(
    public socketService: SocketService,
    public authService: AuthService
  ) {}

  ngOnInit() {
    // Update custom bid amount when auction changes
    this.subscriptions.push(
      this.socketService.onNomination.subscribe(auction => {
        this.customBidAmount.set(auction.currentBid + 1);
        this.bidOnBehalfUserId.set(null);
      }),
      this.socketService.onBidUpdate.subscribe(({ amount }) => {
        this.customBidAmount.set(amount + 1);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  }

  // Commissioner actions
  startDraft() {
    this.socketService.startDraft(this.leagueId);
  }

  advanceDraft() {
    this.socketService.advanceDraft(this.leagueId);
  }

  // Player nomination
  onSearchInput(query: string) {
    this.playerSearchQuery.set(query);

    // Clear previous debounce
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }

    if (!query || query.length < 2) {
      this.searchResults.set([]);
      return;
    }

    // Debounce search
    this.searchDebounce = setTimeout(() => {
      this.searchPlayers(query);
    }, 300);
  }

  async searchPlayers(query: string) {
    this.isSearching.set(true);
    try {
      const response = await fetch(
        `/api/leagues/${this.leagueId}/available-players?query=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const players = await response.json();
        this.searchResults.set(players);
      }
    } catch (error) {
      console.error('Error searching players:', error);
    } finally {
      this.isSearching.set(false);
    }
  }

  selectPlayer(player: Player) {
    this.selectedPlayer.set(player);
    this.searchResults.set([]);
    this.playerSearchQuery.set(`${player.first_name} ${player.last_name}`);
  }

  clearSelection() {
    this.selectedPlayer.set(null);
    this.playerSearchQuery.set('');
    this.searchResults.set([]);
    this.startingBid.set(1);
  }

  nominatePlayer() {
    const player = this.selectedPlayer();
    if (player) {
      this.socketService.nominatePlayer(this.leagueId, player.id, this.startingBid());
      this.clearSelection();
    }
  }

  nominatePlayerOnBehalf() {
    const player = this.selectedPlayer();
    const nominatorInfo = this.currentNominatorInfo();
    if (player && nominatorInfo) {
      this.socketService.nominatePlayer(
        this.leagueId,
        player.id,
        this.startingBid(),
        nominatorInfo.userId
      );
      this.clearSelection();
      this.showCommissionerOverride.set(false);
    }
  }

  // Bidding
  placeBid(amount: number) {
    const onBehalfOf = this.bidOnBehalfUserId();
    if (onBehalfOf) {
      const max = this.bidOnBehalfMaxBid();
      if (max !== null && amount > max) return;
      this.socketService.placeBid(this.leagueId, amount, onBehalfOf);
    } else {
      if (this.maxBid !== null && amount > this.maxBid) return;
      this.socketService.placeBid(this.leagueId, amount);
    }
  }

  isBidOverMax(amount: number): boolean {
    const onBehalfOf = this.bidOnBehalfUserId();
    if (onBehalfOf) {
      const max = this.bidOnBehalfMaxBid();
      return max !== null && amount > max;
    }
    return this.maxBid !== null && amount > this.maxBid;
  }

  effectiveMaxBid(): number | null {
    const onBehalfOf = this.bidOnBehalfUserId();
    return onBehalfOf ? this.bidOnBehalfMaxBid() : this.maxBid;
  }

  incrementBid() {
    const current = this.customBidAmount();
    const max = this.effectiveMaxBid();
    if (max === null || current < max) {
      this.customBidAmount.set(current + 1);
    }
  }

  decrementBid() {
    const current = this.customBidAmount();
    const minBid = (this.socketService.currentAuction()?.currentBid ?? 0) + 1;
    if (current > minBid) {
      this.customBidAmount.set(current - 1);
    }
  }

  placeCustomBid() {
    const amount = this.customBidAmount();
    const currentBid = this.socketService.currentAuction()?.currentBid || 0;
    if (amount > currentBid) {
      this.placeBid(amount);
    }
  }

  getOrdinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
}
